import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloudUpload, RefreshCw, CheckCircle2, WifiOff, AlertTriangle, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { onSyncState, getPendingCount, getErrorCount, syncNow, isSessionExpired } from "@/lib/sync";
import { useOnline } from "@/lib/useOnline";

export function SyncPill({ className }) {
  const navigate = useNavigate();
  const online = useOnline();
  const [pending, setPending] = useState(0);
  const [errors, setErrors] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [expired, setExpired] = useState(isSessionExpired());

  useEffect(() => {
    getPendingCount().then(setPending);
    getErrorCount().then(setErrors);
    const off = onSyncState((s) => {
      setSyncing(s.phase === "syncing" || s.phase === "draining");
      setExpired(s.phase === "auth_expired" || isSessionExpired());
      getPendingCount().then(setPending);
      getErrorCount().then(setErrors);
    });
    return off;
  }, []);

  let label = "Sincronizado";
  let Icon = CheckCircle2;
  // Instrument-panel styles per state
  let cls = "border-border bg-card text-foreground";
  let dot = "bg-[hsl(var(--state-present))]";

  // Fix 3 (hotfix 31/07/2026): si hay pendientes, el pill SIEMPRE lo muestra
  // — nunca "Sincronizado" — para que nunca se pierda de vista que hay datos
  // sin subir, sin importar si está sincronizando o sin señal en ese instante.
  if (expired) {
    // Fix I: con la sesión vencida el pill lo dice — nunca "Sincronizando…"
    // eterno. El cartel de arriba es el que ofrece renovar la sesión.
    label = "Sesión vencida"; Icon = ShieldAlert;
    cls = "border-destructive/40 bg-destructive/10 text-destructive"; dot = "bg-destructive";
  } else if (errors > 0) {
    label = `${errors} con error`; Icon = AlertTriangle;
    cls = "border-destructive/40 bg-destructive/10 text-destructive"; dot = "bg-destructive";
  } else if (pending > 0) {
    label = syncing ? `Subiendo… quedan ${pending}` : `${pending} sin subir`;
    Icon = syncing ? RefreshCw : CloudUpload;
    cls = "border-[hsl(var(--brand-ochre)/0.45)] bg-[hsl(var(--brand-ochre)/0.14)] text-[hsl(var(--brand-ochre))]";
    dot = "bg-[hsl(var(--brand-ochre))]";
  } else if (!online) {
    label = "Sin conexión"; Icon = WifiOff;
    cls = "border-border bg-muted text-foreground"; dot = "bg-muted-foreground";
  } else if (syncing) {
    label = "Sincronizando…"; Icon = RefreshCw;
    cls = "border-[hsl(var(--state-present)/0.4)] bg-[hsl(var(--state-present-bg))] text-[hsl(var(--state-present))]";
    dot = "bg-[hsl(var(--state-present))]";
  }

  // Fix 4 (bug de UX — el indicador rojo no hacía nada al tocarlo en móvil):
  // con errores, tocar el pill navega a la lista de errores accionables en
  // vez de solo relanzar la sincronización en silencio.
  const handleClick = () => {
    if (errors > 0) navigate("/ajustes/sync#errores");
    else syncNow();
  };

  return (
    <button
      onClick={handleClick}
      data-testid="sync-status-pill"
      title={errors > 0 ? "Tocar para ver y resolver los errores" : "Tocar para sincronizar ahora"}
      className={cn(
        "inline-flex items-center gap-1.5 pl-2 pr-2.5 h-9 rounded-full border font-mono text-[11px] tracking-tight transition-colors duration-150 ring-focus",
        cls, className
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} aria-hidden />
      <Icon size={13} className={syncing ? "spin-slow" : ""} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
